<template>
  <RouterLink
    v-if="tagName === 'RouterLink'"
    :to="routeTo"
    :class="btnClasses"
    :title="htmlDecode(btnTitle)"
  >
    <SvgIcon
      v-if="!isRightIcon"
      :icon="icon"
      :class="iconClasses"
    />
    <span
      :class="spanClasses"
      v-html="text"
    />
    <SvgIcon
      v-if="isRightIcon"
      :icon="icon"
      :class="iconClasses"
    />
  </RouterLink>
  <a
    v-else-if="tagName === 'a'"
    :href="url"
    :class="btnClasses"
    target="_blank"
    :title="htmlDecode(btnTitle)"
  >
    <SvgIcon
      v-if="!isRightIcon"
      :icon="icon"
      :class="iconClasses"
    />
    <span
      :class="spanClasses"
      v-html="text"
    />
    <SvgIcon
      v-if="isRightIcon"
      :icon="icon"
      :class="iconClasses"
    />
  </a>
  <button
    v-else-if="tagName === 'button'"
    :class="btnClasses"
    :title="htmlDecode(btnTitle)"
  >
    <SvgIcon
      v-if="!isRightIcon"
      :icon="icon"
      :class="iconClasses"
    />
    <span
      :class="spanClasses"
      v-html="text"
    />
    <SvgIcon
      v-if="isRightIcon"
      :icon="icon"
      :class="iconClasses"
    />
  </button>
</template>

<script>
import SvgIcon from '@/components/SvgIcon.vue'
import { mapGetters } from 'vuex'

export default {
  name: 'MultiBtn',

  components: {
    SvgIcon
  },

  props: {
    routeTo: {
      type: Object,
      default: () => {}
    },
    url: {
      type: String,
      default: ''
    },
    text: {
      type: String,
      default: ''
    },
    label: {
      type: String,
      default: ''
    },
    direction: {
      type: String,
      default: 'right'
    },
    icon: {
      type: String,
      default: ''
    },
    size: {
      type: String,
      default: 'normal'
    },
    theme: {
      type: String,
      default: 'button_style_1'
    },
    isNav: {
      type: Boolean,
      default: undefined
    }
  },

  computed: {
    // Data
    ...mapGetters([ 'myStore' ]),
    isLargeSize() { return this.size === 'large' },
    isRightIcon() { return this.direction === 'right' },
    isBorderless() { return this.theme === 'button_style_4' },
    // Button theme
    btnStyles() {
      const matchTheme = this.myStore(['styles', 'button_themes', this.theme]) ?? false
      if (!matchTheme) { console.error(`Theme '${this.theme}' not found in 'button_themes'`) }
      return matchTheme
    },
    focusStyles() { return (this.btnStyles || {}).focus },
    hoverStyles() { return (this.btnStyles || {}).hover },
    // Attributes
    tagName() {
      if (!this.text) { return }
      if (this.routeTo) { return 'RouterLink' }
      if (this.url) { return 'a' }
      return 'button'
    },
    // Parent
    btnClasses() {
      const { isNav, isLargeSize, isRightIcon, isBorderless, btnStyles, focusStyles, hoverStyles } = this
      if (!btnStyles || !focusStyles || !hoverStyles) { return }
      // Dimensions
      const baseMinHeight = '10_5'
      const lgMinHeight = isLargeSize ? '12_5' : baseMinHeight
      const heightClasses = `min-h-${baseMinHeight} lg:min-h-12_5${lgMinHeight}`
      // Padding X
      const baseFullPxSize = 6
      const lgFullPxSize = isLargeSize ? 8 : baseFullPxSize
      const baseHalfPxSize = 4
      const lgHalfPxSize = isLargeSize ? 5 : baseHalfPxSize
      const plClasses = `${isNav ? 'xs:' : ''}pl-${isRightIcon ? baseFullPxSize : baseHalfPxSize} lg:pl-${isRightIcon ? lgFullPxSize : lgHalfPxSize}`
      const prClasses = `${isNav ? 'xs:' : ''}pr-${isRightIcon ? baseHalfPxSize : baseFullPxSize} lg:pr-${isRightIcon ? lgHalfPxSize : lgFullPxSize}`
      const pxClasses = isBorderless ? 'px-2_25' :  `${isNav ? 'px-2_25' : ''} ${plClasses} ${prClasses}`
      // Padding Y
      const pySize = 2
      const pyClasses = `py-${pySize}`
      const paddingClasses = `${pxClasses} ${pyClasses}`
      // Border Defaults
      const borderWidth = btnStyles.border_width ?? (isBorderless ? 0 : 2)
      const borderColor = isBorderless ? 'transparent' : 'black'
      const borderRadius = btnStyles.border_radius ?? (isBorderless ? 0 : 5)
      // Return
      return [
        'group',
        // Position
        'relative',
        // Dimensions
        heightClasses,
        // Margin
        `mr-4 last:mr-0`,
        // Padding
        paddingClasses,
        // Display
        'inline-flex items-center',
        // Borders
        `border-${borderWidth} border-${btnStyles.border_color ?? borderColor}`,
        `focus:border-${focusStyles.border_color ?? borderColor} lg:hover:border-${hoverStyles.border_color ?? borderColor}`,
        `rounded-${borderRadius}`,
        // Bg
        !isBorderless ? `bg-${btnStyles.bg_color}` : '',
        !isBorderless ? `focus:bg-${focusStyles.bg_color} lg:hover:bg-${hoverStyles.bg_color}` : '',
        // Text
        `text-${btnStyles.text_color}`,
        `focus:text-${focusStyles.text_color} lg:hover:text-${hoverStyles.text_color}`,
        // Fill
        `fill-${btnStyles.fill_color}`,
        `focus:fill-${focusStyles.fill_color} lg:hover:fill-${hoverStyles.fill_color}`,
        // Outline
        'focus:outline-none',
        // Transitions
        'transition-colors duration-500 ease-in-out'
      ]
    },
    btnTitle() { return this.label || this.text },
    // Text
    spanClasses() {
      const { isNav, isLargeSize, btnStyles } = this
      if (!btnStyles) { return }
      // Display
      const displayClasses = isNav ? 'hidden xs:inline-block' : ''
      // Font size
      const fontSizeClasses = isLargeSize ? 'lg:text-xl' : ''
      // return
      return [
        // Dimensions
        'w-full',
        // Display
        displayClasses,
        // Size
        fontSizeClasses,
        // Family
        `font-${btnStyles.font_family}`,
        // Weight
        `font-${btnStyles.font_family}-${btnStyles.font_weight}`,
        // Text transform
        btnStyles.text_transform ?? 'normal-case',
      ]
    },
    // Icon
    iconClasses() {
      const { icon, isNav, isLargeSize, isRightIcon, isBorderless } = this
      // Dimensions
      const baseSize = 5
      const mdSize = 6
      const lgSize = isLargeSize ? 8 : mdSize
      const widthClasses = `w-${baseSize} md:w-${mdSize} lg:w-${lgSize}`
      const heightClasses = `h-${baseSize} md:h-${mdSize} lg:h-${lgSize}`
      const dimensionClasses = `${widthClasses} ${heightClasses}`
      // Margin
      const mDirection = isRightIcon ? 'l' : 'r'
      const baseMarginSize = 3
      const mdMarginSize = 4
      const lgMarginSize = isLargeSize ? 5 : mdMarginSize
      const marginClasses = `${isNav ? 'xs:' : ''}m${mDirection}-${baseMarginSize} md:m${mDirection}-${mdMarginSize} lg:m${mDirection}-${lgMarginSize}`
      // Return
      return [
        dimensionClasses,
        marginClasses,
      ]
    }
  }
}
</script>
